import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { JHALA_GLOBAL_CSS, TopStrip, Header, Footer, Reveal, TagChip } from "components/jhala/Layout.js";

const mernPlans = [
  {
    title: "STARTER MVP",
    price: "$1,500 - $3,000",
    features: [
      "5-8 core features to prove value fast",
      "Responsive UX ready for launch day",
      "Secure basic authentication",
      "Database setup & hosting wiring",
      "Launch in 4-6 weeks, lean & efficient",
    ],
    ctaLabel: "Get Started",
    ctaLink: "/contact-us",
  },
  {
    title: "STANDARD WEB APP",
    price: "$3,000 - $10,000",
    features: [
      "10-15 polished features with UX flows",
      "Custom UI/UX for conversion & retention",
      "Advanced authentication & roles",
      "Third-party integrations wired quickly",
      "Ops-ready admin dashboard",
      "8-12 weeks to production with QA",
    ],
    ctaLabel: "Get Started",
    ctaLink: "/contact-us",
    highlight: true,
  },
  {
    title: "ENTERPRISE SOLUTION",
    price: "Custom Pricing",
    features: [
      "Unlimited features with prioritization",
      "Scalable architecture for growth",
      "Advanced security & compliance",
      "Multiple integrations delivered in parallel",
      "Dedicated support & success cadence",
      "Timeline tailored to aggressive goals",
    ],
    ctaLabel: "Contact Sales",
    ctaLink: "/contact-us",
  },
];

const shopifyPlans = [
  {
    title: "STORE SETUP",
    price: "$700 - $1,200",
    features: [
      "Theme install + fast brand fit",
      "Up to 25 products uploaded for launch",
      "Payment & shipping setup done right",
      "Mobile speed optimization",
      "Go live in 1-2 weeks",
    ],
    ctaLabel: "Get Started",
    ctaLink: "/contact-us",
  },
  {
    title: "CUSTOM STORE",
    price: "$1,200 - $3,000",
    features: [
      "Custom theme for your brand",
      "Advanced customizations without bloat",
      "2-3 custom features to lift revenue",
      "Core Web Vitals speed tuning",
      "SEO setup for quick wins",
      "3-4 weeks to launch",
    ],
    ctaLabel: "Get Started",
    ctaLink: "/contact-us",
    highlight: true,
  },
  {
    title: "SHOPIFY APP",
    price: "$2,500+",
    features: [
      "Custom Shopify app to extend your store",
      "Admin panel integration for control",
      "API integrations built quickly",
      "Developer-friendly documentation",
      "App store submission handled",
      "6-8 weeks build timeline",
    ],
    ctaLabel: "Contact Us",
    ctaLink: "/contact-us",
  },
];

const plansByCategory = {
  mern: { label: "MERN Stack Development", headline: "Choose the plan that fits your product stage" },
  shopify: { label: "Shopify Development", headline: "Launch, scale, or extend your Shopify store" },
};

function PlanCard({ plan, delay }) {
  return (
    <Reveal delay={delay}>
      <div
        className="svc-card"
        style={{
          border: `1px solid ${plan.highlight ? "#4CE0B3" : "#DCD8CC"}`,
          borderRadius: 8,
          padding: "28px 24px",
          background: "#FFFFFF",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {plan.highlight && (
          <div style={{ position: "absolute", top: -12, left: 24 }}>
            <TagChip tone="signal">#MOST-POPULAR</TagChip>
          </div>
        )}
        <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: "#8B93A7", letterSpacing: "0.04em", marginTop: plan.highlight ? 10 : 0 }}>{plan.title}</div>
        <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 26, fontWeight: 700, color: "#0A0F1E", margin: "8px 0 20px" }}>{plan.price}</div>
        <ul style={{ listStyle: "none", padding: 0, margin: "0 0 24px", display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
          {plan.features.map((f) => (
            <li key={f} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13.5, color: "#4A5468", lineHeight: 1.5 }}>
              <CheckCircle2 size={16} style={{ color: "#4CE0B3", marginTop: 1, flexShrink: 0 }} />
              {f}
            </li>
          ))}
        </ul>
        <Link
          to={plan.ctaLink}
          className="cta-btn"
          style={{
            textAlign: "center",
            textDecoration: "none",
            padding: "12px 20px",
            borderRadius: 4,
            fontWeight: 600,
            fontSize: 14,
            cursor: "pointer",
            background: plan.highlight ? "#FF5C39" : "transparent",
            color: plan.highlight ? "#0A0F1E" : "#0A0F1E",
            border: plan.highlight ? "none" : "1px solid #DCD8CC",
          }}
        >
          {plan.ctaLabel}
        </Link>
        <p style={{ fontSize: 12, color: "#8B93A7", marginTop: 12, marginBottom: 0, textAlign: "center" }}>Timeline & scope tailored per engagement.</p>
      </div>
    </Reveal>
  );
}

export default function PricingPage() {
  const [activeCategory, setActiveCategory] = useState("shopify");
  const plans = activeCategory === "mern" ? mernPlans : shopifyPlans;
  const { label, headline } = plansByCategory[activeCategory];

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: "#F6F4EE", color: "#0A0F1E" }}>
      <style>{JHALA_GLOBAL_CSS}</style>
      <TopStrip />
      <Header />

      {/* HERO */}
      <section style={{ background: "#0A0F1E", color: "#F6F4EE", padding: "64px 24px 56px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(#23304A 1px, transparent 1px), linear-gradient(90deg, #23304A 1px, transparent 1px)", backgroundSize: "48px 48px", opacity: 0.2, maskImage: "radial-gradient(ellipse at 70% 30%, black, transparent 70%)" }} />
        <div style={{ maxWidth: 1180, margin: "0 auto", position: "relative" }}>
          <Reveal>
            <TagChip>$ jhalatech --pricing</TagChip>
            <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "clamp(30px, 4.4vw, 46px)", lineHeight: 1.1, margin: "18px 0 14px" }}>
              Simple, transparent pricing.
            </h1>
            <p style={{ fontSize: 16, color: "#B8BFCF", maxWidth: 560, lineHeight: 1.65 }}>
              Starting ranges for common project types — every engagement gets a scoped quote after a
              free discovery call.
            </p>
          </Reveal>
        </div>
      </section>

      {/* TOGGLE + PLANS */}
      <section style={{ padding: "70px 24px 90px", background: "#F6F4EE" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <Reveal>
            <div style={{ textAlign: "center", marginBottom: 44 }}>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: "#8B93A7" }}>{label}</span>
              <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(22px, 2.8vw, 30px)", fontWeight: 700, margin: "10px 0 24px" }}>
                {headline}
              </h2>
              <div style={{ display: "inline-flex", background: "#FFFFFF", border: "1px solid #DCD8CC", borderRadius: 999, padding: 4 }}>
                <button
                  onClick={() => setActiveCategory("mern")}
                  style={{
                    padding: "9px 20px", borderRadius: 999, border: "none", cursor: "pointer",
                    fontWeight: 600, fontSize: 13.5, transition: "background 0.2s ease, color 0.2s ease",
                    background: activeCategory === "mern" ? "#0A0F1E" : "transparent",
                    color: activeCategory === "mern" ? "#F6F4EE" : "#4A5468",
                  }}
                >
                  MERN Plans
                </button>
                <button
                  onClick={() => setActiveCategory("shopify")}
                  style={{
                    padding: "9px 20px", borderRadius: 999, border: "none", cursor: "pointer",
                    fontWeight: 600, fontSize: 13.5, transition: "background 0.2s ease, color 0.2s ease",
                    background: activeCategory === "shopify" ? "#0A0F1E" : "transparent",
                    color: activeCategory === "shopify" ? "#F6F4EE" : "#4A5468",
                  }}
                >
                  Shopify Plans
                </button>
              </div>
            </div>
          </Reveal>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 24 }} className="pricing-toggle-grid">
            {plans.map((plan, i) => (
              <PlanCard key={plan.title} plan={plan} delay={i * 0.08} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: "70px 24px", background: "#0A0F1E", color: "#F6F4EE", textAlign: "center" }}>
        <Reveal>
          <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(22px, 3vw, 30px)", fontWeight: 700, margin: "0 0 14px" }}>
            Not sure which plan fits?
          </h2>
          <p style={{ color: "#B8BFCF", fontSize: 15, marginBottom: 26 }}>
            Every quote is scoped to your actual project — talk to us before committing to a plan.
          </p>
          <Link to="/contact-us" className="cta-btn" style={{ background: "#4CE0B3", color: "#0A0F1E", border: "none", padding: "13px 24px", borderRadius: 4, fontWeight: 600, fontSize: 14, cursor: "pointer", textDecoration: "none", display: "inline-block" }}>
            Book a Free Consultation
          </Link>
        </Reveal>
      </section>

      <Footer />
    </div>
  );
}
